import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Tabs,
    Tab,
    Stack,
    Button,
    CircularProgress,
    Typography,
} from "@mui/material";
import { Close, Download } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { useGetOrderInvoice } from "../../../hooks/orders/useGetOrderInvoice";

/**
 * Embedded invoice viewer for an order.
 *   - Fetches the PDF as a blob (auth header attached automatically)
 *   - AR / EN tabs re-fetch with `?lang=...`
 *   - Download button delivers the same blob as a file
 */
const InvoiceViewerDialog = ({ open, order, onClose }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || "en").startsWith("ar");

    const [lang, setLang] = useState(isArabic ? "ar" : "en");
    const { blobUrl, isFetching, fetchInvoice, download, revoke } = useGetOrderInvoice(
        order?.id
    );

    // (Re)fetch whenever the dialog opens or the language tab changes.
    useEffect(() => {
        if (open && order?.id) {
            fetchInvoice(lang);
        }
        if (!open) {
            revoke();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, lang, order?.id]);

    const filename = `invoice-${order?.invoice_number || order?.order_number || order?.id}-${lang}.pdf`;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{
                sx: {
                    height: { xs: "100%", md: "92vh" },
                    m: { xs: 0, md: 2 },
                    borderRadius: { xs: 0, md: 3 },
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 1.5,
                    pe: 1.5,
                    borderBottom: (t) => `1px solid ${t.palette.divider}`,
                }}
            >
                <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="h6" fontWeight={700}>
                        {isArabic ? "الفاتورة" : "Invoice"}
                    </Typography>
                    {(order?.invoice_number || order?.order_number) && (
                        <Typography variant="body2" color="text.secondary">
                            #{order.invoice_number || order.order_number}
                        </Typography>
                    )}
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                    <Tabs
                        value={lang}
                        onChange={(_e, v) => setLang(v)}
                        textColor="primary"
                        indicatorColor="primary"
                        sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}
                    >
                        <Tab value="ar" label="العربية" />
                        <Tab value="en" label="English" />
                    </Tabs>

                    <Button
                        variant="contained"
                        startIcon={<Download />}
                        onClick={() => download(lang, filename)}
                        disabled={isFetching || !blobUrl}
                        sx={{ borderRadius: 2, fontWeight: "bold", whiteSpace: "nowrap" }}
                    >
                        {isArabic ? "تحميل" : "Download"}
                    </Button>

                    <IconButton onClick={onClose}>
                        <Close />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ p: 0, position: "relative", bgcolor: "#525659" }}>
                {isFetching && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 2,
                            bgcolor: "rgba(0,0,0,0.45)",
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}

                {blobUrl ? (
                    <Box
                        component="iframe"
                        title="invoice"
                        src={blobUrl}
                        sx={{ width: "100%", height: "100%", border: 0, display: "block" }}
                    />
                ) : (
                    !isFetching && (
                        <Box
                            sx={{
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "common.white",
                            }}
                        >
                            <Typography variant="body2">
                                {isArabic ? "تعذر تحميل الفاتورة." : "Could not load invoice."}
                            </Typography>
                        </Box>
                    )
                )}
            </DialogContent>
        </Dialog>
    );
};

export default InvoiceViewerDialog;
